import React, { useState, useEffect } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/functions";
import firebaseApp, { db } from "services/firebase";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress, Typography, TextField, Alert } from "@mui/material";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";
import { LoadingButton } from "@mui/lab";
import {
  ChatRounded,
  MedicalServicesRounded,
  LocalHospitalRounded,
  MedicalInformationRounded,
  AdminPanelSettingsRounded,
} from "@mui/icons-material";
// US emergency-department search (Algolia-backed) is parked for the Kenya-only
// rollout — the Algolia application is blocked, and the picker now reads the
// curated Kenyan list straight out of Firestore.
// import EmergencySearch from "components/EmergencySearch";
import FacilitySearch from "components/FacilitySearch";
import AddressSearch from "components/AddressSearch";
import { getAddress } from "utils/helpers/getAddress";
import { getStateCode } from "utils/helpers/getStateCode";
import * as geohash from "ngeohash";
import { buildBaseUserDoc } from "./buildBaseUserDoc";

const STEPS = {
  ACCOUNT_TYPE: 0,
  FACILITY_TYPE: 1,
  FACILITY_INFO: 2,
};

const fallbackOrganizationName = (email) => {
  const seed = (email || "").split("@")[0].trim();
  if (!seed) return "New Organization";
  return `${seed}'s Organization`;
};

const AccountSetupFlow = ({
  prefilledEmail = null,
  onComplete,
  onStepChange,
  backHandlerRef,
  onBackFromFirstStep,
  preselectedRole = null,
  /**
   * Skip the role-chooser screen and auto-create the user profile on mount.
   * Used by the invitation flow (caller already knows the invited role).
   * Ignored when preselectedRole is "facility" since facility setup needs more input.
   */
  autoCreateProfile = false,
  /**
   * True when the user is signing up to accept an org invitation. Admins joining
   * this way attach to the inviter's org (via acceptInvitation), so we must NOT
   * mint a new org for them here.
   */
  isInvitationFlow = false,
  onSignupStart,
  /** Called after signup batch completes or fails (pair with onSignupStart). */
  onSignupEnd,
}) => {
  const shouldAutoCreate =
    autoCreateProfile &&
    preselectedRole &&
    preselectedRole !== "facility";
  // Facility signup goes straight to the facility picker. The Emergency
  // Department / Clinic chooser (STEPS.FACILITY_TYPE) is parked for the
  // Kenya-only rollout: the Clinic path depends on Mapbox address lookup, which
  // Kenya has no street addressing for, so every facility is picked from the
  // curated list instead.
  const initialStep = preselectedRole
    ? preselectedRole === "facility"
      ? STEPS.FACILITY_INFO
      : STEPS.ACCOUNT_TYPE
    : STEPS.ACCOUNT_TYPE;
  const [step, setStepInternal] = useState(initialStep);
  const [loading, setLoading] = useState(shouldAutoCreate);
  const [error, setError] = useState("");
  const [selectedRole, setSelectedRole] = useState(preselectedRole || "");

  // Wrapper to notify parent of step changes
  const setStep = (newStep) => {
    setStepInternal(newStep);
    onStepChange?.(newStep);
  };

  // Sync preselectedRole on mount - ensures step is correct even with React quirks
  useEffect(() => {
    if (preselectedRole) {
      const expectedStep =
        preselectedRole === "facility"
          ? STEPS.FACILITY_INFO
          : STEPS.ACCOUNT_TYPE;
      if (selectedRole !== preselectedRole) {
        setSelectedRole(preselectedRole);
      }
      if (step === STEPS.ACCOUNT_TYPE) {
        setStepInternal(expectedStep);
        onStepChange?.(expectedStep);
        return;
      }
    }
    onStepChange?.(step);
  }, []);

  // Invitation flow: caller already knows the role, so skip the chooser
  // entirely and write the user doc on mount.
  useEffect(() => {
    if (!shouldAutoCreate) return;
    const email = prefilledEmail || firebaseApp.auth().currentUser?.email || "";
    createUserProfile(preselectedRole, email);
    // Run once on mount; createUserProfile is stable enough for this purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      // "hospital" = pick an existing facility and file a claim. Fixed while the
      // Clinic (address-entry) path is parked; see the initialStep note above.
      facilityType: "hospital",
      facilityName: "",
      address: null,
      emergency_location: null,
    },
  });

  const watchFacilityType = watch("facilityType");

  const createUserProfile = async (role, email) => {
    setLoading(true);
    setError("");
    onSignupStart?.();

    try {
      const user = firebaseApp.auth().currentUser;
      if (!user) throw new Error("No authenticated user found.");
      const roleToSave = role === "provider" ? "professional" : role;

      if (roleToSave === "admin" && isInvitationFlow) {
        // Admin invited into an existing org: create a bare admin profile with
        // no org. acceptInvitation attaches organizationId (as non-owner).
        await db
          .collection("users")
          .doc(user.uid)
          .set(buildBaseUserDoc({ user, email, role: "admin" }));
      } else if (roleToSave === "admin") {
        const orgName = fallbackOrganizationName(email || user.email);
        const batch = db.batch();
        const userRef = db.collection("users").doc(user.uid);
        const orgRef = db.collection("organizations").doc();

        batch.set(orgRef, {
          id: orgRef.id,
          name: orgName,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          createdBy: user.uid,
          seats: { total: 0, used: 0 },
          subscriptionOwnerId: user.uid,
        });

        batch.set(userRef, {
          ...buildBaseUserDoc({
            user,
            email,
            role: "admin",
          }),
          organizationId: orgRef.id,
          isOrganizationOwner: true,
          joinedOrganizationAt: firebase.firestore.FieldValue.serverTimestamp(),
        });

        await batch.commit();
      } else {
        await db
          .collection("users")
          .doc(user.uid)
          .set(buildBaseUserDoc({ user, email, role: roleToSave }));
      }

      await onComplete?.(user);
    } catch (err) {
      console.error("Error creating profile:", err);
      setError(err.message || "Failed to create profile.");
    } finally {
      setLoading(false);
      onSignupEnd?.();
    }
  };

  const handleAccountTypeContinue = async () => {
    if (!selectedRole) {
      setError("Please choose an account type.");
      return;
    }
    if (selectedRole === "facility") {
      setStep(STEPS.FACILITY_INFO);
      return;
    }
    const email = prefilledEmail || firebaseApp.auth().currentUser?.email || "";
    await createUserProfile(selectedRole, email);
  };

  const handleFacilityInfoSubmit = async (data) => {
    setLoading(true);
    setError("");
    onSignupStart?.();

    try {
      const user = firebaseApp.auth().currentUser;
      if (!user) throw new Error("No authenticated user found.");

      const facilityType = data.facilityType;
      const locationId =
        facilityType === "hospital"
          ? String(data.emergency_location?.id)
          : db.collection("locations").doc().id;

      const userRef = db.collection("users").doc(user.uid);
      const userDoc = buildBaseUserDoc({
        user,
        email: prefilledEmail || user.email,
        role: "facility",
        extra: { location: locationId },
      });

      if (facilityType === "hospital") {
        // Joining an EXISTING facility grants access to that facility's patient
        // PHI (check-in visits, virtual queue), so we never write
        // locations.users from the client. Create the user, then file a claim
        // that a platform admin approves on the Approval page — approval is what
        // adds the user to the facility's members.
        await userRef.set(userDoc);
        const requestFacilityClaim = firebaseApp
          .functions()
          .httpsCallable("requestFacilityClaim");
        await requestFacilityClaim({ locationId });
      } else {
        const addressData = data.address;
        const lookup = (name) =>
          addressData.context?.find((a) => a?.id?.startsWith(name))?.text;
        const formattedAddress = getAddress(addressData);
        const latitude = addressData.center[1];
        const longitude = addressData.center[0];
        const geopoint = new firebase.firestore.GeoPoint(latitude, longitude);
        const hash = geohash.encode(latitude, longitude);

        // Brand-new clinic: created empty and pending, owned by its creator.
        // Safe to write client-side — there is no existing PHI, and an admin
        // approves it before it goes live.
        const batch = db.batch();
        batch.set(userRef, userDoc);
        batch.set(db.collection("locations").doc(locationId), {
          id: locationId,
          title: data.facilityName,
          lat: latitude,
          lng: longitude,
          address: formattedAddress,
          city: lookup("place") || lookup("locality"),
          // Kenya has no state short_code to parse (and no postcodes in
          // practice), so fall back to the region name, which is the county.
          state: getStateCode(addressData) || lookup("region"),
          zip: lookup("postcode"),
          type: "Clinic",
          users: firebase.firestore.FieldValue.arrayUnion(user.uid),
          status: "pending",
          coordinates: geopoint,
          g: { geohash: hash, geopoint },
        });
        await batch.commit();
      }

      await onComplete?.(user);
    } catch (err) {
      console.error("Error creating facility:", err);
      setError(err.message || "Failed to create facility.");
    } finally {
      setLoading(false);
      onSignupEnd?.();
    }
  };

  const handleBack = () => {
    if (step === STEPS.ACCOUNT_TYPE) {
      onBackFromFirstStep?.();
      return;
    }
    // FACILITY_INFO is now the first facility step, so back leaves the flow
    // (invite/share arrivals) or returns to the account-type chooser.
    if (step === STEPS.FACILITY_TYPE || step === STEPS.FACILITY_INFO) {
      if (preselectedRole) {
        onBackFromFirstStep?.();
      } else {
        setStep(STEPS.ACCOUNT_TYPE);
        setSelectedRole("");
        setError("");
      }
    }
  };

  useEffect(() => {
    if (backHandlerRef) backHandlerRef.current = handleBack;
  });

  if (shouldAutoCreate) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: 240,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Setting up your account...
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {step === STEPS.ACCOUNT_TYPE && (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Choose your account type
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select how you&apos;ll use HealthDesk.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SelectableChoiceCard
              icon={<ChatRounded fontSize="large" />}
              title="Patient"
              description="Get symptom insights, find care, and manage your health journey."
              onClick={() => {
                setSelectedRole("patient");
                setError("");
              }}
              selected={selectedRole === "patient"}
            />
            <SelectableChoiceCard
              icon={<MedicalServicesRounded fontSize="large" />}
              title="Healthcare Provider"
              description="Access clinical decision support and patient management tools."
              onClick={() => {
                setSelectedRole("provider");
                setError("");
              }}
              selected={selectedRole === "provider"}
            />
            <SelectableChoiceCard
              icon={<LocalHospitalRounded fontSize="large" />}
              title="Healthcare Organization"
              description="Manage your facility, staff, and streamline patient flow."
              onClick={() => {
                setSelectedRole("facility");
                setError("");
              }}
              selected={selectedRole === "facility"}
            />
            <SelectableChoiceCard
              icon={<AdminPanelSettingsRounded fontSize="large" />}
              title="ChartMind Admin"
              description="Manage prompts, billing, and user access for your organization."
              onClick={() => {
                setSelectedRole("admin");
                setError("");
              }}
              selected={selectedRole === "admin"}
            />
          </Box>
          <LoadingButton
            variant="contained"
            size="large"
            fullWidth
            loading={loading}
            disabled={!selectedRole}
            onClick={handleAccountTypeContinue}
            sx={{ py: 1.5, mt: 3 }}
          >
            Continue
          </LoadingButton>
        </>
      )}

      {/* Parked for the Kenya-only rollout (unreachable: no step routes here).
          Restore by pointing facility signup at STEPS.FACILITY_TYPE again once
          Kenyan clinics have a way to give a location without a street address. */}
      {step === STEPS.FACILITY_TYPE && (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            What type of facility?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select the type of healthcare facility you&apos;re registering.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SelectableChoiceCard
              orientation="vertical"
              icon={<LocalHospitalRounded sx={{ fontSize: 40 }} />}
              title="Emergency Department"
              description="24-hour emergency care"
              onClick={() => {
                setValue("facilityType", "hospital", { shouldDirty: true });
                setStep(STEPS.FACILITY_INFO);
              }}
            />
            <SelectableChoiceCard
              orientation="vertical"
              icon={<MedicalInformationRounded sx={{ fontSize: 40 }} />}
              title="Clinic"
              description="Urgent/immediate care"
              onClick={() => {
                setValue("facilityType", "urgent-care", { shouldDirty: true });
                setStep(STEPS.FACILITY_INFO);
              }}
            />
          </Box>
        </>
      )}

      {step === STEPS.FACILITY_INFO && (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Facility details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {watchFacilityType === "hospital"
              ? "Search for your facility by name or county."
              : "Enter your facility name and address."}
          </Typography>
          <form onSubmit={handleSubmit(handleFacilityInfoSubmit)}>
            {watchFacilityType === "hospital" ? (
              /* US: <EmergencySearch control={control} errors={errors} /> */
              <FacilitySearch control={control} errors={errors} />
            ) : (
              <>
                <Controller
                  name="facilityName"
                  control={control}
                  rules={{ required: "Facility name is required." }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Facility Name"
                      fullWidth
                      variant="outlined"
                      error={!!errors.facilityName}
                      helperText={errors.facilityName?.message}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <AddressSearch control={control} errors={errors} />
              </>
            )}
            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              loading={loading}
              sx={{ py: 1.5, mt: 3 }}
            >
              Complete registration
            </LoadingButton>
          </form>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export { STEPS as ACCOUNT_SETUP_STEPS };
export default AccountSetupFlow;
